import { useEffect, useState } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { analytics } from "../../analytics";
import { useLocationWrapper } from "../../hooks/useLocationWrapper";

import UnsupportedManufacturerLogo from "../../assets/unsupported-manufacturer-logo.svg";
import Button from "../ButtonComponent";
import TextInput from "../TextInput";
import { useParams } from "react-router";
import { Manufacturer } from "../../interfaces";
import { FEATURE_FLAGS } from "../../constants";

// we are missing the device name, display_name prop or something here to be used in the title
interface IState extends Manufacturer {
  redirectUrl?: string;
}

const apiUrl = import.meta.env.VITE_CONNECT_API_BASE_URL || "http://localhost:3000";

export function ConnectUnsupported() {
  const { linkToken = "" } = useParams();
  const { state } = useLocationWrapper<IState>();

  const threeCECopyEnabled = useFeatureIsOn(FEATURE_FLAGS.threeCECopyEnabled);

  const [customManufacturerName, setCustomManufacturerName] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const hasManufacturer = state && state.slug;
  const redirectUrl = state?.redirectUrl || "";

  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const storeUnsupportedManufacturer = async (slug: string): Promise<void> => {
    try {
      await fetch(`${apiUrl}/v1/states/${linkToken}/unsupportedManufacturer/${slug}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  useEffect(() => {
    if (analytics && analytics.page) {
      analytics.page("connect unsupported");
    }

    if (!hasManufacturer) {
      return;
    }

    storeUnsupportedManufacturer(state.slug);
  }, [hasManufacturer, linkToken, state?.slug]);

  const handleContinue = () => {
    const url = new URL(redirectUrl);
    window.location.assign(url);
  };

  const handleCustomManufacturerSubmit = async () => {
    const trimmedName = customManufacturerName.trim();
    if (!trimmedName) return;

    setIsSubmitting(true);

    const slug = slugify(trimmedName);

    try {
      if (analytics && analytics.track) {
        analytics.track("connect custom manufacturer selected", {
          manufacturer: slug,
          displayName: trimmedName,
        });
      }
    } catch (error) {
      console.error("Failed to track event: ", error);
    }

    try {
      await storeUnsupportedManufacturer(slug);
      setIsSubmitted(true);
    } catch (err) {
      // Error already logged in storeUnsupportedManufacturer
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasManufacturer && !isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-between gap-8 tp:gap-4 max-w-[40rem]">
        <img src={UnsupportedManufacturerLogo} className="mx-auto w-24 h-24" alt="unsupported-logo" />
        <div className="text-texturePennBlue font-bold text-xl d:text-4xl tp:text-2xl text-center">
          Tell us your manufacturer
        </div>
        <div className="text-texturePennBlue text-center mb-5">
          We&apos;ll record your manufacturer and notify you when support becomes available.
        </div>
        <form className="w-full sm:w-[30rem] flex flex-col gap-4" action={handleCustomManufacturerSubmit}>
          <TextInput
            value={customManufacturerName}
            onChange={setCustomManufacturerName}
            placeholder="Enter manufacturer name"
            maxLength={100}
          />
          <Button type="submit" disabled={!customManufacturerName.trim() || isSubmitting} loading={isSubmitting}>
            Submit
          </Button>
        </form>
      </div>
    );
  }

  const displayName = hasManufacturer ? state.displayName : customManufacturerName;

  return (
    <>
      {threeCECopyEnabled ? (
        <div className="flex flex-col items-center justify-between gap-8 tp:gap-4 max-w-[40rem]">
          <img src={UnsupportedManufacturerLogo} className="mx-auto w-24 h-24" alt="success-logo" />
          <div className="text-texturePennBlue font-bold text-xl d:text-4xl tp:text-2xl text-center">
            Your Application is Complete!
          </div>
          <div>
            <div className="text-texturePennBlue text-center mb-5">
              {displayName} is not currently supported by Texture, however, your application may qualify for 3CE's
              Residential Battery Rebate Program. Your application is under review. Status updates will be emailed to
              you from{" "}
              <a className="text-textureIris underline" href="mailto:programs@3ce.org" target="_blank">
                programs@3ce.org
              </a>
              .
            </div>
            <div className="text-texturePennBlue text-center mb-5">
              If you have any questions please{" "}
              <a className="text-textureIris" href="mailto:programs@3ce.org" target="_blank">
                reach out to us
              </a>
              .
            </div>
            <div className="text-texturePennBlue text-center mb-5">Thank you for your feedback!</div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-between gap-8 tp:gap-4 max-w-[40rem]">
          <img src={UnsupportedManufacturerLogo} className="mx-auto w-24 h-24" alt="success-logo" />
          <div className="text-texturePennBlue font-bold text-xl d:text-4xl tp:text-2xl text-center">
            Support for {displayName} Coming Soon!
          </div>
          <div>
            <div className="text-texturePennBlue text-center mb-5">
              Thank you for choosing {displayName}. We&apos;ll notify you as soon as this manufacturer is fully
              supported to connect your devices.{" "}
            </div>
            <div className="text-texturePennBlue text-center mb-5">
              If you have any questions, please{" "}
              <a
                className="text-textureIris cursor-pointer"
                target="_blank"
                href="https://www.texturehq.com/contact-us"
              >
                reach out to us
              </a>
              .
            </div>
            <div className="text-texturePennBlue text-center mb-5">Thank you for your feedback!</div>
          </div>
        </div>
      )}
      <div className="w-full sm:w-[30rem] flex flex-col items-center gap-8 tp:gap-4">
        {redirectUrl && <Button onClick={handleContinue}>Continue</Button>}
      </div>
    </>
  );
}
